/*
AlertDialog component attributes:

onCancel [function]: callback handler triggered when the user dismisses the alert dialog without confirmation
onConfirm [function]: callback handler triggered when the user clicks on the confirmation button
allowOutsideClickClose [boolean]: if true, the alert dialog can be dismissed by clicking outside of the modal content
isBusy [boolean]: if true, elements within the modal will be disabled and it will not be dismissible
headerText [string]: text that will appear in the header of the modal
variant [string]: variant name (ex: 'danger')
buttonCancelText [string]: text to display on the cancel button
buttonConfirmText [string]: text to display on the confirm button
buttonConfirmBusyText [string]: if set, the confirm button will display this loading text when "isBusy" is set to true
*/

import m from 'mithril';
import Portal from 'mithril-portal';
import Button, { BUTTON_VARIANTS } from '@shared/components-v2/button';
import IconXClose from '@client-shared/icons-v2/x-close'

import s from './alert-dialog.module.scss';

export const ALERT_DIALOG_VARIANTS = {
	DANGER: 'danger'
};

const AlertDialog = {};

AlertDialog.view = (vnode) => {
	const {
		onCancel = () => console.warn('onCancel handler not assigned to AlertDialog'),
		onConfirm = () => console.warn('onConfirm handler not assigned to AlertDialog'),
		allowOutsideClickClose = true,
		isBusy = false,
		headerText = 'Alert',
		variant = '',
		buttonCancelText = 'Cancel',
		buttonConfirmText = 'Confirm',
		buttonConfirmBusyText = '',
		showCancel = true
	} = vnode.attrs;

	let primaryButtonVariant = BUTTON_VARIANTS.PRIMARY;

	switch (variant) {
		case ALERT_DIALOG_VARIANTS.DANGER:
			primaryButtonVariant = BUTTON_VARIANTS.DANGER;
			break;

		default:
			break;
	}

	const handleOutsideClick = () => {
		if (!allowOutsideClickClose || isBusy) {
			return;
		}

		onCancel();
	}

	const handleBtnCancel = () => {
		if (isBusy) {
			return;
		}

		onCancel();
	}

	const handleBtnConfirm = () => {
		if (isBusy) {
			return;
		}

		onConfirm();
	}

	const primaryButtonAttrs = {
		variant: primaryButtonVariant,
		text: buttonConfirmText,
		loadingText: buttonConfirmBusyText,
		rounded: false,
		onclick: handleBtnConfirm
	};

	if (isBusy) {
		if (buttonConfirmBusyText) {
			primaryButtonAttrs.loading = true;
		} else {
			primaryButtonAttrs.disabled = true;
		}
	}

	return m(Portal, [
		m('div', { class: s.dialog_container }, [
			m('div', { class: s.backdrop, onclick: handleOutsideClick }),
			m('div', { class: s.dialog_content }, [
				m('header', { class: s.header }, [
					m('span', { class: s.header_text }, headerText),
					m('button', { class: s.header_close, type: 'button', onclick: handleBtnCancel }, IconXClose)
				]),
				m('div', { class: s.body }, Array.isArray(vnode.children) && vnode.children.length
					? vnode.children
					: m('p', 'Error: no child components added to AlertDialog')
				),
				m('footer', { class: s.footer }, [
					showCancel && m('div', { class: s.footer_left }, m(Button, { variant: BUTTON_VARIANTS.SECONDARY, text: buttonCancelText, disabled: isBusy, rounded: false, onclick: handleBtnCancel })),
					m('div', { class: s.footer_right }, m(Button, primaryButtonAttrs))
				])
			])
		])
	]);
}

export default AlertDialog
